import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../logo/swarovski-1.png';

// ── helpers ────────────────────────────────────────────────────────────────
const NumberToWords = (num) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens  = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convert = (n) => {
        if (n < 10)       return units[n];
        if (n < 20)       return n === 10 ? 'Ten' : teens[n - 11];
        if (n < 100)      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
        if (n < 1000)     return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
        if (n < 100000)   return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
        if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
        return 'Number is too large';
    };
    if (num === 0) return 'Zero';
    return convert(num);
};

const amountInWords = (amount) => {
    const formatted = parseFloat(amount).toFixed(2);
    const [rupees, paise] = formatted.split('.');
    let words = `Rupees ${NumberToWords(parseInt(rupees))} Only`;
    if (paise !== '00') words += ` and ${NumberToWords(parseInt(paise))} Paise Only`;
    return words;
};

const fmt2    = (v) => (parseFloat(v) || 0).toFixed(2);
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

// ── main export ────────────────────────────────────────────────────────────
const generateRepairPDF = (labelData) => {
    if (!labelData) return;

    // Normalise API response shape
    const data = Array.isArray(labelData) ? labelData[0] : labelData;
    const rp   = data?.RepairAndReplacementPrint   ?? data;
    const det  = data?.RepairAndReplacementPrintDetails ?? [];

    if (!rp || !rp.CompanyName) {
        console.error('generateRepairPDF: unexpected data shape', labelData);
        return;
    }

    // ── Document setup ────────────────────────────────────────────────────
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const PW  = doc.internal.pageSize.getWidth();   // 210 mm
    const PH  = doc.internal.pageSize.getHeight();  // 297 mm
    const ML  = 5;
    const MR  = 5;
    const CW  = PW - ML - MR;                       // 200 mm

    let y = 6;

    // ── Logo ──────────────────────────────────────────────────────────────
    try {
        const imgW = 22, imgH = 22;
        doc.addImage(logo, 'PNG', (PW - imgW) / 2, y, imgW, imgH);
        y += imgH + 2;
    } catch (_) { y += 4; }

    // ── Company header (centred, bold) ────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Dealer : SWAROVSKI', PW / 2, y, { align: 'center' }); y += 5;

    doc.setFontSize(11);
    doc.text(rp.CompanyName || '', PW / 2, y, { align: 'center' }); y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    if (rp.AddressLine) {
        const addrLines = doc.splitTextToSize(rp.AddressLine, CW - 10);
        addrLines.forEach(l => { doc.text(l, PW / 2, y, { align: 'center' }); y += 4; });
    }
    doc.text(`Phone No:${rp.ContactNumber1 || ''}`, PW / 2, y, { align: 'center' }); y += 4;
    doc.text(`Email:${rp.Email || ''}`, PW / 2, y, { align: 'center' }); y += 5;

    // ── Meta row: State | CIN / GST | Receipt No / Date ──────────────────
    const c1x = ML;
    const c2x = ML + CW * 0.38;
    const c3x = ML + CW * 0.72;

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');

    doc.text(`State Code :${rp.BranchStateCode || ''}`, c1x, y);
    doc.text(`Receipt Number :${rp.BillPrefix}/${rp.BillNumber}/${rp.FinYear}`, c3x, y); y += 4.5;

    doc.text(`State Name :${rp.BranchStateName || ''}`, c1x, y);
     if (rp.CINNumber) doc.text(`CIN Number :${rp.CINNumber || ''}`, c2x, y - 2.25);
    doc.text(`GST Number :${rp.CompanyGSTNumber || ''}`, c2x, y + 2.25);
    doc.text(`Date :${fmtDate(rp.BillDate)}`,            c3x, y); y += 4.5;

    y += 5;

    // ── RECEIPT title ─────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('RECEIPT', PW / 2, y, { align: 'center' }); y += 5;

    // ── Customer box ──────────────────────────────────────────────────────
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);

    const custLines = [];
    custLines.push({ text: 'Customer Name & Address', bold: true });
    custLines.push({ text: rp.CustomerName || '',     bold: false });
    custLines.push({ text: rp.ContactNumber || '',    bold: false });
    if (rp.CustomerAddress) custLines.push({ text: rp.CustomerAddress, bold: false });
    if (rp.GSTNumber)       custLines.push({ text: `GST Number: ${rp.GSTNumber}`, bold: false });

    const custBoxH = Math.max(18, custLines.length * 5 + 4);
    doc.rect(ML, y, CW, custBoxH);
    let cy = y + 5;
    custLines.forEach(cl => {
        doc.setFont('helvetica', cl.bold ? 'bold' : 'normal');
        doc.setFontSize(9);
        doc.text(cl.text, ML + 2, cy);
        cy += 4.5;
    });
    y += custBoxH;

    // ── Items table ───────────────────────────────────────────────────────
    const tableBody = det.map(r => [
        r.ReceiptTypeName    || '',
        r.ItemCategoryName   || '',
        r.ItemName           || '',
        r.Remarks            || '',
        (parseFloat(r.Amount) || 0) !== 0 ? fmt2(r.Amount) : '',
    ]);

    autoTable(doc, {
        startY: y,
        head: [['ITEM DESCRIPTION', 'ITEM CATEGORY', 'ITEM DETAILS', 'REMARKS', 'AMOUNT']],
        body: tableBody,
        theme: 'grid',
        styles: {
            fontSize: 8,
            cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
            font: 'helvetica',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.25,
            minCellHeight: 7,
        },
        headStyles: {
            fillColor:  [255, 255, 255],
            textColor:  [0, 0, 0],
            fontStyle:  'bold',
            fontSize:   8,
            halign:     'center',
            lineColor:  [0, 0, 0],
            lineWidth:  0.3,
        },
        columnStyles: {
            0: { cellWidth: 38, halign: 'left'  },   // ITEM DESCRIPTION
            1: { cellWidth: 35, halign: 'left'  },   // ITEM CATEGORY
            2: { cellWidth: 60, halign: 'left'  },   // ITEM DETAILS
            3: { cellWidth: 42, halign: 'left'  },   // REMARKS
            4: { cellWidth: 25, halign: 'right' },   // AMOUNT
        },
        margin: { left: ML, right: MR },
    });

    y = doc.lastAutoTable.finalY;

    // ── GST breakdown + Total Amount rows ────────────────────────────────
    const labelColW = CW * 0.8;
    const valueColW = CW - labelColW;
    const rowH      = 7;

    doc.setLineWidth(0.3);
    doc.setDrawColor(0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);

    const gstRate      = parseFloat(rp.GSTRate)      || 18;
    const taxableAmt   = parseFloat(rp.TaxableAmount) || 0;
    const cgstAmt      = parseFloat(rp.CGSTAmount)    || 0;
    const sgstAmt      = parseFloat(rp.SGSTAmount)    || 0;
    const igstAmt      = parseFloat(rp.IGSTAmount)    || 0;
    const roundOff     = parseFloat(rp.RoundOff)      || 0;
    const isIGST       = igstAmt > 0;

    const gstRows = [
        { label: 'Taxable Amount',                        value: fmt2(taxableAmt) },
        isIGST
            ? { label: `IGST @ ${gstRate}%`,              value: fmt2(igstAmt)   }
            : { label: `CGST @ ${gstRate / 2}%`,          value: fmt2(cgstAmt)   },
        ...(!isIGST ? [{ label: `SGST @ ${gstRate / 2}%`, value: fmt2(sgstAmt)   }] : []),
        { label: 'Round Off',                             value: fmt2(roundOff)  },
    ];

    gstRows.forEach(row => {
        // left open border (matches table style)
        doc.line(ML,             y,          ML,              y + rowH);
        doc.line(ML,             y,          ML + labelColW,  y);
        doc.line(ML,             y + rowH,   ML + labelColW,  y + rowH);
        // value cell full box
        doc.rect(ML + labelColW, y, valueColW, rowH);

        doc.setFont('helvetica', 'normal');
        doc.text(row.label, ML + labelColW - 2, y + rowH / 2 + 1.5, { align: 'right' });
        doc.text(row.value, ML + labelColW + valueColW - 2, y + rowH / 2 + 1.5, { align: 'right' });
        y += rowH;
    });

    // ── Total Amount row ──────────────────────────────────────────────────
    const totalBoxH = 9;

    doc.setLineWidth(0.3);
    doc.setDrawColor(0);

    doc.line(ML,              y,              ML,              y + totalBoxH);
    doc.line(ML,              y,              ML + labelColW,  y);
    doc.line(ML,              y + totalBoxH,  ML + labelColW,  y + totalBoxH);
    doc.rect(ML + labelColW, y, valueColW, totalBoxH);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('TOTAL AMOUNT', ML + labelColW - 2, y + totalBoxH / 2 + 1.5, { align: 'right' });
    doc.setFontSize(10);
    doc.text(fmt2(rp.TotalAmount), ML + labelColW + valueColW - 2, y + totalBoxH / 2 + 1.5, { align: 'right' });

    y += totalBoxH;

    // ── Amount in words + Authorised Signatory ────────────────────────────
    const wordsColW = CW * 0.65;
    const sigColW   = CW - wordsColW;
    const words     = amountInWords(rp.TotalAmount);
    const wordLines = doc.splitTextToSize(words, wordsColW - 4);
    const wordsBoxH = Math.max(14, wordLines.length * 4 + 6);

    doc.setLineWidth(0.3);
    // Words cell (left + top + bottom, open right)
    doc.line(ML,              y,              ML,              y + wordsBoxH);
    doc.line(ML,              y,              ML + wordsColW,  y);
    doc.line(ML,              y + wordsBoxH,  ML + wordsColW,  y + wordsBoxH);

    // Signatory cell (full box)
    doc.rect(ML + wordsColW, y, sigColW, wordsBoxH);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(wordLines, ML + 2, y + 5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Authorised Signatory',
        ML + wordsColW + sigColW / 2, y + wordsBoxH - 3, { align: 'center' });

    y += wordsBoxH + 6;  

    // ── Open print dialog ─────────────────────────────────────────────────
    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const win = window.open(blobUrl, '_blank');
    if (win) {
        win.addEventListener('load', () => { win.focus(); win.print(); });
    }
};

export default generateRepairPDF;
